import { types, flow } from 'mobx-state-tree';
import { ClientJS } from 'clientjs';

import api from 'services/API';
import { Notification, UserDevice, Alarm } from 'models/types';
import { getRootStore } from 'models/root';
import orderBy from 'lodash/orderBy';

import { stringUtilities, numberUtilities } from 'utils';
import NotificationService from 'services/notificationService';

const client = new ClientJS();

const NotificationWithView = Notification.views(self => ({
  get active() {
    return self._conditions_closed === false;
  },

  get resolved() {
    return self._conditions_closed === true;
  },

  get condition() {
    const root = getRootStore();
    return root.conditionsStore.all.find(condition => condition.id === self.condition_id);
  },

  get conditionAlarmId() {
    return self.condition?.alarm_id;
  },

  get alarm() {
    const root = getRootStore();

    return root.notificationsStore.alarms.find(({ id }) => id === self._conditions_alarm_id);
  },

  get alarmType() {
    return self._alarms_alert_caption.includes('High') ? 'high' : 'low';
  },

  get sensorAlarms() {
    const root = getRootStore();

    return root.notificationsStore.alarms.filter(
      alarm => alarm.operand_id === self.alarm?.operand_id,
    );
  },

  get criteriaValue() {
    return Number.isNaN(+self._conditions_context?.temp_c)
      ? null
      : numberUtilities.celsiusWrapper(self._conditions_context?.temp_c, { signsAmount: 1 });
  },

  get lowAlarm() {
    return self.sensorAlarms.find(a => a?.rule.includes('<'));
  },

  get highAlarm() {
    return self.sensorAlarms.find(a => a?.rule.includes('>'));
  },

  get minRaw() {
    return self.lowAlarm?.rule ? stringUtilities.ruleToValue(self.lowAlarm?.rule) : null;
  },

  get min() {
    return Number.isNaN(+self.minRaw)
      ? null
      : numberUtilities.celsiusWrapper(self.minRaw, { signsAmount: 1 });
  },

  get maxRaw() {
    return self.highAlarm?.rule ? stringUtilities.ruleToValue(self.highAlarm?.rule) : null;
  },

  get max() {
    return Number.isNaN(+self.maxRaw)
      ? null
      : numberUtilities.celsiusWrapper(self.maxRaw, { signsAmount: 1 });
  },

  get defaultMin() {
    const value = stringUtilities.ruleToValue(self.lowAlarm?.default_rule);

    return value ? numberUtilities.celsiusWrapper(value, { signsAmount: 1 }) : null;
  },
  get defaultMax() {
    const value = stringUtilities.ruleToValue(self.highAlarm?.default_rule);

    return value ? numberUtilities.celsiusWrapper(value, { signsAmount: 1 }) : null;
  },
}));

export const notificationInitialState = {
  isLoaded: false,
  all: [],
  alarms: [],
  selectedConditionStateFilter: null,
  devices: [],
  archivedDevices: [],
  deviceFilters: {
    sortedBy: 'device_name',
    orderBy: 'desc',
  },
  searchString: '',
  state: 'done',
};

export const notificationModel = types
  .model({
    isLoaded: types.boolean,
    all: types.array(NotificationWithView),
    alarms: types.array(Alarm),
    devices: types.array(UserDevice),
    archivedDevices: types.array(UserDevice),
    deviceFilters: types.maybeNull(
      types.model({
        sortedBy: types.enumeration('sortedBy', ['device_name', 'type', 'created_at']),
        orderBy: types.enumeration('orderBy', ['asc', 'desc']),
      }),
    ),
    selectedConditionStateFilter: types.maybeNull(
      types.enumeration('selectedConditionStateFilter', ['active', 'resolved']),
    ),
  })
  .views(self => ({
    get sorted() {
      return self.all.slice().sort((a, b) => {
        if (a.active && b.resolved) {
          return -1;
        } else if (a.resolved && b.active) {
          return 1;
        }
        return Date.parse(b._conditions_happened_from) - Date.parse(a._conditions_happened_from);
      });
    },

    get filtered() {
      return self.sorted
        .slice()
        .filter(notification => {
          if (self.selectedConditionStateFilter === null) return true;
          return notification[self.selectedConditionStateFilter];
        })
        .sort((a, b) => {
          // Push _conditions_closed items to bottom
          const aClosed = Boolean(a._conditions_closed);
          const bClosed = Boolean(b._conditions_closed);

          if (aClosed && !bClosed) return 1;
          if (!aClosed && bClosed) return -1;

          // Sort by created_at (newest first)
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;

          // Then by statusCode (0 first)
          if (a.statusCode === 0 && b.statusCode !== 0) return -1;
          if (a.statusCode !== 0 && b.statusCode === 0) return 1;

          return 0;
        });
    },

    get filteredConditions() {
      let conditions = [];
      if (self.conditionsFilterValue === null) {
        conditions = [...self.conditions];
      } else if (self.conditionsFilterValue === 'active') {
        conditions = [...self.conditions.filter(c => !c._closed)];
      } else if (self.conditionsFilterValue === 'resolved') {
        conditions = [...self.conditions.filter(c => c._closed)];
      }
      return conditions.sort((a, b) => Date.parse(b.happened_from) - Date.parse(a.happened_from));
    },

    get deviceItems() {
      const root = getRootStore();

      let filtered = self.devices.filter(device => device.user_id === root.userStore.profile.id);
      if (self.searchString) {
        const searchArray = self.searchString?.toLowerCase().split(' ');

        filtered = filtered.filter(item =>
          searchArray.every(element => item.device_name.includes(element)),
        );
      }

      const result = orderBy(filtered, [self.deviceFilters.sortedBy], [self.deviceFilters.orderBy]);
      return result;
    },

    get unread() {
      return self.all
        .filter(n => n.status_code === 0)
        .sort(
          (a, b) =>
            Date.parse(b._conditions_happened_from) - Date.parse(a._conditions_happened_from),
        );
    },

    get unreadCount() {
      return self.unread.length;
    },

    get fingerprint() {
      return client.getFingerprint();
    },

    get isDeviceExist() {
      return Boolean(self.devices.find(device => device.device_fingerprint === self.fingerprint));
    },
  }))
  .actions(self => ({
    fetchDevices: flow(function* () {
      try {
        self.state = 'pending';
        const response = yield api.getUserDevices();

        if (response?.data?.result) {
          self.devices.replace(response.data.result);
          self.state = 'done';
          self.isLoaded = true;
          self.updated = new Date();
          return response.data.result;
        } else {
          self.isLoaded = false;
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.isLoaded = false;
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),
    patchUserDevices: flow(function* (id, data) {
      try {
        self.state = 'pending';
        const response = yield api.patchUserDevices(id, data);

        if (response?.data) {
          self.devices.replace([
            ...self.devices.filter(e => e.id !== response.data.row.id),
            response.data.row,
          ]);
          self.state = 'done';
          self.isLoaded = true;
          self.updated = new Date();
          return response.data.result;
        } else {
          self.isLoaded = false;
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.isLoaded = false;
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),

    registerUserDevice: flow(function* ({ device_name, subscription, notifications_muted }) {
      try {
        self.state = 'pending';

        const response = yield api.registerUserDevice({
          device_name,
          subscription,
          device_fingerprint: self.fingerprint,
          notifications_muted,
        });

        if (response?.data) {
          self.devices.replace([...self.devices, response.data.result[0]]);
          self.state = 'done';
          self.isLoaded = true;
          self.updated = new Date();
          return response.data.result;
        } else {
          self.isLoaded = false;
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.isLoaded = false;
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),

    removeDevice: flow(function* (item) {
      try {
        self.state = 'pending';
        const response = yield api.deleteUserDevice(item.id);

        if (response?.data) {
          self.devices.replace([...self.devices.filter(e => e.id !== response.data.row.id)]);
          if (localStorage.getItem('bar_track_pwa_device_identifier') === item.identifier) {
            localStorage.removeItem('bar_track_pwa_device_identifier');
          }
          self.state = 'done';
          self.isLoaded = true;
          self.updated = new Date();
          return response.data.row;
        } else {
          self.isLoaded = false;
          self.state = 'done';
          self.updated = new Date();
        }
      } catch (error) {
        self.isLoaded = false;
        self.state = 'error';
        self.updated = new Date();
        console.error(error);
        return Promise.reject(error);
      }
    }),
    markAsRead: flow(function* (ids) {
      if (ids.length === 0) return;

      try {
        const response = yield api.patchNotification(
          { status_code: 1 },
          { id: ids, rows: ids.length },
        );
        const updatedNotifications = response.data?.result;
        const updatedIds = updatedNotifications.map(({ id }) => id);

        self.all.replace([
          ...self.all.filter(e => !updatedIds.includes(e.id)),
          ...updatedNotifications,
        ]);
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    }),

    testDeviceNotification: flow(function* (device_id) {
      try {
        yield api.testUserDeviceNotification(device_id);
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    }),

    getConditionById: flow(function* (id) {
      try {
        const response = yield api.getConditionById(id);
        return response.data.result;
      } catch (error) {
        console.error(error);
        return Promise.reject(error);
      }
    }),

    resubscribeDevice: async ({ device_id, device_fingerprint }) => {
      if (!device_id || !device_fingerprint || device_fingerprint !== self.fingerprint) return;

      const device = [...self.devices, ...self.archivedDevices].find(
        device => device.device_fingerprint === self.fingerprint && device.id === device_id,
      );

      const subscription = await NotificationService.subscribeDevice();

      if (!subscription) return;

      await self.registerUserDevice({
        device_name: device.device_name,
        notifications_muted: device.notifications_muted,
        subscription,
      });

      self.setUserDevices([
        ...[...self.devices, ...self.archivedDevices].filter(e => e.id !== device.id),
        { ...device, archived: true },
      ]);
    },

    setUserDevices(user_devices) {
      self.devices.replace(user_devices.filter(device => !device.archived));
      self.archivedDevices.replace(user_devices.filter(device => device.archived));
    },

    checkCurrentSubscription: async () => {
      const hasCurrentSubscription = Boolean(await NotificationService.getCurrentSubscription());
      const isCurrentDeviceActive = Boolean(
        self.devices.find(device => device.device_fingerprint === self.fingerprint),
      );
      const hasCurrentDeviceInArchived = Boolean(
        self.archivedDevices.find(device => device.device_fingerprint === self.fingerprint),
      );

      if (hasCurrentSubscription && !isCurrentDeviceActive && hasCurrentDeviceInArchived) {
        const deviceToResubscribe = self.archivedDevices
          .filter(device => device.device_fingerprint === self.fingerprint)
          .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];

        await self.resubscribeDevice({
          device_id: deviceToResubscribe.id,
          device_fingerprint: self.fingerprint,
        });
      }
    },

    getMinMax(operand_id) {
      const filteredAlarms = self.alarms.filter(alarm => alarm.operand_id === operand_id);

      const lowAlarm = filteredAlarms.find(alarm => alarm?.rule.includes('<'));
      const highAlarm = filteredAlarms.find(alarm => alarm?.rule.includes('>'));

      const minValue = lowAlarm ? stringUtilities.ruleToValue(lowAlarm.rule) : null;
      const maxValue = highAlarm ? stringUtilities.ruleToValue(highAlarm.rule) : null;

      return {
        min: numberUtilities.celsiusWrapper(minValue, { signsAmount: 1 }),
        max: numberUtilities.celsiusWrapper(maxValue, { signsAmount: 1 }),
      };
    },

    upsertNotification(notification) {
      const index = self.all.findIndex(n => n.id === notification.id);
      if (index !== -1) {
        self.all[index] = notification;
      } else {
        self.all.push(notification);
      }
    },

    setNotifications(notifications) {
      self.all.replace(notifications);
    },

    setSearchString(value) {
      self.searchString = value;
    },

    setDeviceFilters(filters) {
      if (filters === null) {
        self.deviceFilters = {
          sortedBy: 'timestamp',
          orderBy: 'desc',
        };
      } else {
        self.deviceFilters = {
          sortedBy: filters.sortedBy || self.deviceFilters.sortedBy,
          orderBy: filters.orderBy || self.deviceFilters.orderBy,
        };
      }
    },

    setAlarms(rows) {
      self.alarms.replace(rows);
    },

    setSelectedConditionStateFilter(filter) {
      self.selectedConditionStateFilter = filter;
    },
  }));
